import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// The Announcer (E09-S8-T3): the one place the surface speaks to a screen reader. It owns two visually
// hidden live regions, polite and assertive, mounted once per view by the Surface root, inside the
// root's own element — never on the page the overlay sits over. Components call announce(text,
// level) through useAnnounce() and never make a live region of their own: no aria-live, no role
// status, alert or log that is left live. The conversation is a log with aria-live off, and the
// karaoke highlight and the captions are plain text, so lighting a sentence up says nothing.
//
//   - What is said is the content as received (a sentence, a nudge's short text, a step's title)
//     or the catalogue's words around it (rule 6). The Announcer never decides what to say.
//   - An announcement may carry a key: what it is about (a nudge id, a step id, a message id and
//     sentence index). Only the first announcement for a key is said, so a state replayed after a
//     reconnect, or a posture change that mounts another component, says nothing a second time.
//     mark(key) takes a key as said without saying it.
//   - Each announcement is appended to its region as its own line, so two in a row are both read,
//     and the same words said twice are said twice. A line is removed CLEAR_MS later: nothing stale
//     waits at the end of the surface for a reading cursor to find, and removing says nothing
//     (aria-relevant is additions).
//   - Nothing is said in the frame the regions are created: a screen reader often takes text that
//     arrives with a new live region as its initial content and skips it. Until one tick after the
//     Announcer mounts, announcements wait in a queue (keys are taken at once, so a replay while
//     waiting still says nothing), then go out in order.
//   - When the Announcer's effect is cleaned up — an unmount, StrictMode's second run, an Activity
//     hidden — its timers are cleared and so are its lines, so no line is left that nothing will
//     remove; announcements made meanwhile queue until it is ready again.
//   - Nothing is logged. A sentence is a transcript; it goes to the region and nowhere else.
//
// Outside an Announcer (a component rendered alone, in a test or a gallery story), announce does
// nothing and returns false.
import { createContext, useContext, useEffect, useRef, useState, } from "react";
import "./announcer.css";
/** How long a line stays in its region after it is said. */
export const CLEAR_MS = 7_000;
/**
 * The most keys an Announcer remembers. A view's keys grow with what its session says; past the
 * bound the oldest is forgotten, which could only make a very old replay speak again.
 */
export const MAX_KEYS = 10_000;
const NONE = Object.freeze({
    announce: () => false,
    mark: () => undefined,
    has: () => false,
});
const AnnouncerContext = createContext(NONE);
/** The Announcer this component is inside; a handle that does nothing outside one. */
export function useAnnouncer() {
    return useContext(AnnouncerContext);
}
/** announce(text, level, key), from the Announcer this component is inside. */
export function useAnnounce() {
    return useContext(AnnouncerContext).announce;
}
/**
 * Says `text` each time `active` turns true while the component is mounted — a status line that
 * appears because of something the user did (the microphone opening, an export failing). Not on
 * mount: a posture change that mounts the component again with the line showing says nothing.
 */
export function useAnnounceWhen(active, text, level = "polite") {
    const announce = useAnnounce();
    const was = useRef(active);
    useEffect(() => {
        if (active && !was.current)
            announce(text, level);
        was.current = active;
    }, [active, text, level, announce]);
}
const EMPTY = { polite: [], assertive: [] };
function createCore(setLines) {
    const keys = new Set();
    const timers = new Set();
    const queue = [];
    let ready = false;
    let next = 0;
    const remember = (key) => {
        keys.add(key);
        if (keys.size > MAX_KEYS) {
            const oldest = keys.values().next();
            if (oldest.done !== true)
                keys.delete(oldest.value);
        }
    };
    const later = (fn, ms) => {
        const timer = setTimeout(() => {
            timers.delete(timer);
            fn();
        }, ms);
        timers.add(timer);
    };
    const say = (text, level) => {
        const line = { id: (next += 1), text };
        setLines((held) => ({ ...held, [level]: [...held[level], line] }));
        later(() => {
            setLines((held) => ({ ...held, [level]: held[level].filter((l) => l !== line) }));
        }, CLEAR_MS);
    };
    return {
        handle: {
            announce(text, level = "polite", key) {
                if (key !== undefined) {
                    if (keys.has(key))
                        return false;
                    remember(key);
                }
                if (text === "")
                    return false;
                if (ready)
                    say(text, level);
                else
                    queue.push({ text, level });
                return true;
            },
            mark: remember,
            has: (key) => keys.has(key),
        },
        start() {
            later(() => {
                ready = true;
                for (const { text, level } of queue.splice(0))
                    say(text, level);
            }, 0);
        },
        stop() {
            ready = false;
            for (const timer of timers)
                clearTimeout(timer);
            timers.clear();
            setLines(() => EMPTY);
        },
    };
}
/** The two regions, and the context components announce through. Mount one per view. */
export function Announcer({ children }) {
    const [lines, setLines] = useState(EMPTY);
    const [core] = useState(() => createCore(setLines));
    const handle = core.handle;
    useEffect(() => {
        core.start();
        return () => {
            core.stop();
        };
    }, [core]);
    return (_jsxs(AnnouncerContext.Provider, { value: handle, children: [children, _jsxs("div", { className: "wp-announcer", "data-announcer": "", children: [_jsx(Region, { level: "polite", lines: lines.polite }), _jsx(Region, { level: "assertive", lines: lines.assertive })] })] }));
}
function Region({ level, lines, }) {
    return (_jsx("div", { "aria-live": level, "aria-relevant": "additions", "aria-atomic": "false", "data-announcer": level, children: lines.map((line) => (_jsx("p", { children: line.text }, line.id))) }));
}
//# sourceMappingURL=announcer.js.map